// src/views/RecurringInvoiceDetailsView.js  recinv-detail-2f5c8a61
// READ-ONLY schedule detail (Seam 2a). The schedule arrives as a prop (viewingX
// pattern, like InvoiceDetailsView). It subscribes live to the invoices THIS
// schedule generated using the ONE index-free shape proven in production: a single
// equality on recurringInvoiceId + limit(), NO orderBy, NO cursor (the orderBy+
// cursor combo threw FAILED_PRECONDITION — see generator recinv-health-6b1e9d40).
// Ordering is done client-side after the read.
import React, { useState, useEffect, useMemo } from 'react';
import { collection, query, where, limit, onSnapshot, doc, updateDoc, arrayUnion, serverTimestamp, Timestamp } from 'firebase/firestore';
import { startOfDay } from 'date-fns';
import { db } from '../firebase/config';
import { ChevronLeft, AlertTriangle, Repeat, Pencil, Pause, Play, SkipForward, Ban } from 'lucide-react';
import { formatCurrency, formatDate, toJsDate } from '../utils/formatters';
import InvoiceStatusBadge from '../components/common/InvoiceStatusBadge';
import RecurringScheduleStatusBadge from '../components/common/RecurringScheduleStatusBadge';
import ConfirmationModal from '../components/common/ConfirmationModal';
import { describeRecurrence, scheduleHealth } from '../utils/recurringInvoices';
import { advanceRecurrence } from '../utils/recurrence';

const RUN_TYPE_LABELS = { auto_send: 'Auto-send (Unpaid)', draft: 'Draft (manual send)' };
const BILLING_MODE_LABELS = { advance: 'In advance', arrears: 'In arrears' };
const GENERATED_SAMPLE = 50;
const NEVER_ENDS_YEAR = 2999;
// recinv-act-3f8b6d02 -- module scope so it isn't recreated every render. Byte-match:
// generator index.js:1491 (NEVER_SENTINEL) + form RECURRENCE_END_SENTINEL. Parking a
// schedule's nextRunDate here drops it OUT of the generator's daily due query.
const NEVER_SENTINEL = new Date('2999-12-31T00:00:00');

const fmtRunDate = (value) => {
    const d = toJsDate(value);
    if (!d || d.getFullYear() >= NEVER_ENDS_YEAR) return '—';
    return formatDate(d);
};

const RecurringInvoiceDetailsView = ({ schedule: scheduleProp, currentUserProfile, onBack, onInvoiceSelect, onEdit }) => {
    // recinv-live-7c4e9a21 -- CrmApp's viewingSchedule is a plain useState (CrmApp.js:129)
    // that never re-reads, so after an action (or a generator-side change) the prop is
    // stale. Seed from the prop, then subscribe to the doc so the view is authoritative.
    const [liveSchedule, setLiveSchedule] = useState(scheduleProp);
    const schedule = liveSchedule || scheduleProp;
    const [generated, setGenerated] = useState([]);
    const [confirmAction, setConfirmAction] = useState(null); // the pending action config; doubles as the modal open flag (ExpensesView pattern)
    const [isProcessing, setIsProcessing] = useState(false);
    const [actionError, setActionError] = useState(null);

    useEffect(() => {
        setGenerated([]);
        const orgId = currentUserProfile?.organizationId;
        const schedId = schedule?.id;
        if (!orgId || !schedId) return;
        // INDEX-FREE: equality + limit only. Do NOT add orderBy/cursor here.
        const q = query(
            collection(db, `organizations/${orgId}/invoices`),
            where('recurringInvoiceId', '==', schedId),
            limit(GENERATED_SAMPLE)
        );
        const unsub = onSnapshot(q,
            s => setGenerated(s.docs.map(d => ({ id: d.id, ...d.data() }))),
            () => setGenerated([]));
        return unsub;
    }, [currentUserProfile?.organizationId, schedule?.id]);

    // recinv-live-7c4e9a21 -- subscribe to THIS schedule's doc. Single source of truth:
    // reflects our own action writes AND generator-side changes (nextRunDate advancing,
    // lastError appearing) with no optimistic/actual drift. Prop is only the seed.
    useEffect(() => {
        const orgId = currentUserProfile?.organizationId;
        const schedId = scheduleProp?.id;
        if (!orgId || !schedId) return;
        setLiveSchedule(scheduleProp); // reset when navigating between schedules
        const ref = doc(db, `organizations/${orgId}/recurringInvoices/${schedId}`);
        const unsub = onSnapshot(ref, snap => { if (snap.exists()) setLiveSchedule({ id: snap.id, ...snap.data() }); });
        return unsub;
        // Keyed on the whole scheduleProp object, not its id: a NEW object identity for the
        // same schedule would resubscribe and briefly reset to the stale prop before the
        // snapshot lands. Harmless today -- CrmApp holds one object per viewed schedule -- but
        // worth knowing if a flicker ever appears here.
    }, [currentUserProfile?.organizationId, scheduleProp]);

    // Client-side newest-first sort (no orderBy in the query).
    const generatedSorted = useMemo(() => {
        return [...generated].sort((a, b) => {
            const ad = toJsDate(a.createdAt)?.getTime() || 0;
            const bd = toJsDate(b.createdAt)?.getTime() || 0;
            return bd - ad;
        });
    }, [generated]);

    // recinv-detail-openinv -- hand the invoice up to CrmApp's setViewingInvoice.
    // Coerce createdAt/dueDate to JS Dates first (mirrors the list mapper,
    // InvoicesView.js:267-268): these docs carry raw Firestore Timestamps and
    // InvoiceDetailsView's formatDate() renders 'N/A' on a raw Timestamp. The early
    // returns in CrmApp check viewingInvoice BEFORE viewingSchedule, so the invoice
    // opens on top; its Back button navigates to /invoices, which clears the schedule
    // (recinv-crmapp-clear-9b2e4d18) and lands on the flat Invoices list.
    const openInvoice = (inv) => {
        if (!onInvoiceSelect) return;
        onInvoiceSelect({ ...inv, createdAt: toJsDate(inv.createdAt), dueDate: toJsDate(inv.dueDate) });
    };

    const lineItems = useMemo(() => schedule?.lineItems || [], [schedule]);
    const totals = useMemo(() => {
        const subtotal = lineItems.reduce((a, i) => a + ((Number(i.quantity) || 0) * (Number(i.price) || 0)), 0);
        const tax = lineItems.reduce((a, i) => {
            const line = (Number(i.quantity) || 0) * (Number(i.price) || 0);
            const rate = i.taxExempt ? 0 : (i.taxSnapshot?.rate || 0);
            return a + line * (rate / 100);
        }, 0);
        return { subtotal, tax, total: subtotal + tax };
    }, [lineItems]);

    const health = scheduleHealth(schedule);

    const Meta = ({ label, children }) => (
        <div>
            <h3 className="text-xs text-gray-500 uppercase font-semibold">{label}</h3>
            <p className="text-gray-800 font-medium">{children}</p>
        </div>
    );

    // recinv-act-3f8b6d02 -- lifecycle actions. ALL are Firestore UPDATES, never delete:
    // generated invoices back-point to this doc via recurringInvoiceId. No UI role gate --
    // every action here is an update, which the rules already allow isOffice() (End writes
    // status:'ended', an update too), so a UI role check would be cosmetic. isOffice() at
    // the rules layer is the real control. (NEVER_SENTINEL lives at module scope.)
    const orgId = currentUserProfile?.organizationId;
    const status = schedule?.status;
    const anchor = toJsDate(schedule?.startDate) || new Date();
    const currentNextRun = toJsDate(schedule?.nextRunDate);

    // Next lattice point ON OR AFTER today, walked from the anchor with the schedule's own
    // (always-Custom) recurrence -- same engine + anchor the generator uses (index.js:1531).
    const computeResumeDate = () => {
        const today0 = startOfDay(new Date());
        let cursor = startOfDay(anchor), guard = 0;
        while (cursor && cursor < today0 && guard++ < 5000) cursor = advanceRecurrence(cursor, schedule?.recurrence, anchor);
        return cursor || null;
    };

    const buildPause = () => ({
        confirmText: 'Pause schedule', processingText: 'Pausing…',
        errorText: 'Could not pause this schedule. Please try again.',
        title: 'Pause this recurring invoice?',
        message: 'Billing stops until you resume. No invoices generate while paused, and you can resume any time.',
        patch: {
            status: 'paused',
            // LOAD-BEARING, not tidiness: park nextRunDate at the 2999 sentinel. If it stayed
            // in the past, resume would re-enter the generator's daily range query (index.js:1508)
            // and back-bill ONE invoice per missed run, each dated in the past -- a 3-month pause
            // would emit 3 invoices for months the customer wasn't served. The sentinel drops the
            // doc OUT of that query. Resume recomputes a fresh forward nextRunDate.
            nextRunDate: Timestamp.fromDate(NEVER_SENTINEL),
            pausedAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
        },
    });

    const buildResume = () => {
        const resumeDate = computeResumeDate();
        if (!resumeDate) return null; // malformed recurrence -- guarded at the call site
        return {
            confirmText: 'Resume schedule', processingText: 'Resuming…',
            errorText: 'Could not resume this schedule. Please try again.',
            title: 'Resume this recurring invoice?',
            message: `Billing restarts. The next invoice will generate on ${formatDate(resumeDate)} — never a past date, so no back-dated invoices.`,
            patch: {
                status: 'active',
                nextRunDate: Timestamp.fromDate(startOfDay(resumeDate)),
                pausedAt: null,
                updatedAt: serverTimestamp(),
            },
        };
    };

    const buildSkip = () => {
        const skipped = currentNextRun;
        const newNext = skipped ? advanceRecurrence(skipped, schedule?.recurrence, anchor) : null;
        if (!skipped || !newNext) return null; // no valid next run -- guarded at the call site
        return {
            confirmText: 'Skip this run', processingText: 'Skipping…',
            errorText: 'Could not skip this run. Please try again.',
            title: 'Skip the next invoice?',
            message: `The invoice scheduled for ${formatDate(skipped)} will be skipped. The next invoice will generate on ${formatDate(newNext)} instead.`,
            patch: {
                nextRunDate: Timestamp.fromDate(startOfDay(newNext)),
                // First-ever skipDates entry (form + seed both write []). Firestore Timestamp,
                // matching every other date on these docs; the generator reads it via
                // d.toDate() (index.js:1671). arrayUnion appends without a read.
                skipDates: arrayUnion(Timestamp.fromDate(startOfDay(skipped))),
                updatedAt: serverTimestamp(),
            },
        };
    };

    const buildEnd = () => ({
        // 'End billing — remove schedule' leads with the meaning so it can't be misread as
        // dropping a customer off a list. The label MUST retain the word "remove": that is
        // the ONLY reason ConfirmationModal renders the button red (its test is
        // includes('Delete')||includes('Remove')). Drop "remove" and it silently turns blue
        // on the one permanent action here -- do not "tidy" it out.
        confirmText: 'End billing — remove schedule', processingText: 'Ending…',
        errorText: 'Could not end this schedule. Please try again.',
        title: 'End this recurring invoice for good?',
        message: 'This permanently ends billing for this schedule. Invoices already created stay on record. This can’t be undone — to bill this customer again you’ll create a new schedule.',
        patch: {
            // Matches the generator's own end-branch (index.js:1698-1705) field-for-field so a
            // manual end and an automatic end are indistinguishable.
            status: 'ended',
            endedAt: serverTimestamp(),
            nextRunDate: Timestamp.fromDate(NEVER_SENTINEL),
            lastError: null,
            lastErrorAt: null,
            updatedAt: serverTimestamp(),
        },
    });

    // Open the confirm modal, or surface a visible error if the action can't be computed.
    const requestAction = (built, cannotMsg) => {
        if (!built) { setActionError(cannotMsg); return; }
        setActionError(null);
        setConfirmAction(built);
    };

    const runAction = async () => {
        if (!confirmAction || !orgId || !schedule?.id) return;
        setIsProcessing(true);
        setActionError(null);
        try {
            await updateDoc(doc(db, `organizations/${orgId}/recurringInvoices/${schedule.id}`), confirmAction.patch);
            setConfirmAction(null); // the doc subscription refreshes the view
        } catch (err) {
            console.error('recinv action failed:', err);
            setActionError(confirmAction.errorText || 'That action could not be saved. Please try again.');
            setConfirmAction(null); // close the modal so the red banner is visible behind it
        } finally {
            setIsProcessing(false);
        }
    };

    return (
        <div className="p-4 sm:p-6 md:p-10 bg-gray-50 min-h-screen">
            <div className="max-w-4xl mx-auto">
                <div className="flex justify-between items-center mb-6">
                    <button onClick={onBack} className="flex items-center text-sm font-semibold text-blue-600 hover:text-blue-800">
                        <ChevronLeft size={16} className="mr-1" /> Back to Recurring Invoices
                    </button>
                    <div className="flex items-center gap-3">
                        {onEdit && ( // recinvform-detail-editbtn-9f3c2a71
                            <button onClick={() => onEdit(schedule)} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50">
                                <Pencil size={14} /> Edit schedule
                            </button>
                        )}
                        {/* recinv-actbtns-9a1e5c74 -- lifecycle actions, per-status visibility */}
                        {status === 'active' && (
                            <button onClick={() => requestAction(buildPause(), 'Could not pause this schedule.')} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50">
                                <Pause size={14} /> Pause
                            </button>
                        )}
                        {status === 'paused' && (
                            <button onClick={() => requestAction(buildResume(), 'Could not work out a next run date — check the schedule’s recurrence.')} className="flex items-center gap-1.5 px-3 py-1.5 border border-green-300 rounded-lg text-sm font-semibold text-green-700 hover:bg-green-50">
                                <Play size={14} /> Resume
                            </button>
                        )}
                        {status === 'active' && (
                            <button onClick={() => requestAction(buildSkip(), 'Could not work out the next run date — check the schedule’s recurrence.')} className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-lg text-sm font-semibold text-gray-700 hover:bg-gray-50">
                                <SkipForward size={14} /> Skip next
                            </button>
                        )}
                        {(status === 'active' || status === 'paused') && (
                            <button onClick={() => requestAction(buildEnd(), 'Could not end this schedule.')} className="flex items-center gap-1.5 px-3 py-1.5 border border-red-300 rounded-lg text-sm font-semibold text-red-700 hover:bg-red-50">
                                <Ban size={14} /> End
                            </button>
                        )}
                        <RecurringScheduleStatusBadge status={schedule?.status} />
                    </div>
                </div>

                {/* recinv-acterr-2d6f8b13 -- action failure, user-visible (not console-only) */}
                {actionError && (
                    <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-300 flex items-start gap-3">
                        <AlertTriangle size={18} className="text-red-600 shrink-0 mt-0.5" />
                        <p className="text-sm font-medium text-red-900">{actionError}</p>
                    </div>
                )}

                {/* lastError — prominent red banner (hard failure) */}
                {health.broken && (
                    <div className="mb-4 p-4 rounded-lg bg-red-50 border border-red-300 flex items-start gap-3">
                        <AlertTriangle size={18} className="text-red-600 shrink-0 mt-0.5" />
                        <div>
                            <p className="text-sm font-medium text-red-900">This schedule has stopped generating invoices.</p>
                            <p className="text-sm text-red-800 mt-1">{health.brokenReason}</p>
                            {schedule?.lastErrorAt && (
                                <p className="text-xs text-red-700 mt-1">Last failed {formatDate(toJsDate(schedule.lastErrorAt))}.</p>
                            )}
                        </div>
                    </div>
                )}
                {/* amber attention reasons (only when not broken) */}
                {!health.broken && health.attention.length > 0 && (
                    <div className="mb-4 p-4 rounded-lg bg-amber-50 border border-amber-300 flex items-start gap-3">
                        <AlertTriangle size={18} className="text-amber-500 shrink-0 mt-0.5" />
                        <ul className="text-sm text-amber-800 list-disc pl-4">
                            {health.attention.map((r, i) => <li key={i}>{r}</li>)}
                        </ul>
                    </div>
                )}

                <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8">
                    <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-2 mb-1">
                        <Repeat size={22} /> {schedule?.name || schedule?.title || 'Recurring schedule'}
                    </h1>
                    <p className="text-gray-500 mb-6">{schedule?.customerDetails?.name || schedule?.customerName || '—'}</p>
                    {/* recinv-paused-5b3c9e40 */}
                    {schedule?.status === 'paused' && schedule?.pausedAt && (
                        <p className="text-sm text-amber-600 -mt-4 mb-6">Paused since {formatDate(toJsDate(schedule.pausedAt))}.</p>
                    )}

                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
                        <Meta label="Repeats">{describeRecurrence(schedule?.recurrence)}</Meta>
                        <Meta label="Next Run">{fmtRunDate(schedule?.nextRunDate)}</Meta>
                        <Meta label="Last Run">{schedule?.lastRunDate ? formatDate(toJsDate(schedule.lastRunDate)) : '—'}</Meta>
                        <Meta label="Billing Mode">{BILLING_MODE_LABELS[schedule?.billingMode] || 'In advance'}</Meta>
                        <Meta label="Run Type">{RUN_TYPE_LABELS[schedule?.runType] || 'Draft (manual send)'}</Meta>
                        <Meta label="Ends">{schedule?.recurrence?.neverEnds ? 'Never' : (toJsDate(schedule?.recurrence?.endDate) && toJsDate(schedule.recurrence.endDate).getFullYear() < NEVER_ENDS_YEAR ? formatDate(toJsDate(schedule.recurrence.endDate)) : 'Never')}</Meta>
                    </div>

                    {/* Line items snapshot */}
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Line Items</h3>
                    <table className="w-full text-left mb-6">
                        <thead className="border-b"><tr className="text-xs text-gray-600">
                            <th className="p-2 font-semibold">Item</th>
                            <th className="p-2 font-semibold text-right">Qty</th>
                            <th className="p-2 font-semibold text-right">Price</th>
                            <th className="p-2 font-semibold text-right">Amount</th>
                        </tr></thead>
                        <tbody className="divide-y divide-gray-100 text-sm">
                            {lineItems.map((i, idx) => (
                                <tr key={idx}>
                                    <td className="p-2 text-gray-800">{i.name || '—'}</td>
                                    <td className="p-2 text-right">{Number(i.quantity) || 0}</td>
                                    <td className="p-2 text-right">{formatCurrency(i.price)}</td>
                                    <td className="p-2 text-right">{formatCurrency((Number(i.quantity) || 0) * (Number(i.price) || 0))}</td>
                                </tr>
                            ))}
                            {lineItems.length === 0 && <tr><td colSpan={4} className="p-2 text-gray-400">No line items.</td></tr>}
                        </tbody>
                    </table>
                    <div className="flex justify-end mb-8">
                        <div className="w-48 text-sm space-y-1">
                            <div className="flex justify-between"><span className="text-gray-500">Subtotal</span><span>{formatCurrency(totals.subtotal)}</span></div>
                            <div className="flex justify-between"><span className="text-gray-500">Tax</span><span>{formatCurrency(totals.tax)}</span></div>
                            <div className="flex justify-between font-bold text-gray-900 border-t pt-1"><span>Total</span><span>{formatCurrency(totals.total)}</span></div>
                        </div>
                    </div>

                    {/* Invoices this schedule has generated — rows open the invoice (recinv-detail-openinv) */}
                    <h3 className="text-lg font-semibold text-gray-800 mb-3">Generated Invoices</h3>
                    {generatedSorted.length === 0 ? (
                        <p className="text-sm text-gray-400">No invoices generated yet.</p>
                    ) : (
                        <table className="w-full text-left text-sm">
                            <thead className="border-b"><tr className="text-xs text-gray-600">
                                <th className="p-2 font-semibold">Invoice #</th>
                                <th className="p-2 font-semibold">Date</th>
                                <th className="p-2 font-semibold text-center">Status</th>
                                <th className="p-2 font-semibold text-right">Total</th>
                            </tr></thead>
                            <tbody className="divide-y divide-gray-100">
                                {generatedSorted.map(inv => (
                                    <tr key={inv.id} onClick={() => openInvoice(inv)}
                                        className="cursor-pointer transition-colors bg-white hover:bg-blue-50">
                                        <td className="p-2 font-medium text-gray-900">{inv.invoiceNumber || inv.id.substring(0, 10)}</td>
                                        <td className="p-2">{inv.createdAt ? formatDate(toJsDate(inv.createdAt)) : '—'}</td>
                                        <td className="p-2 text-center"><InvoiceStatusBadge status={inv.status} /></td>
                                        <td className="p-2 text-right">{formatCurrency(inv.total)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                    {generatedSorted.length >= GENERATED_SAMPLE && (
                        <p className="text-xs text-gray-400 mt-2">Showing the first {GENERATED_SAMPLE} generated invoices.</p>
                    )}
                </div>
            </div>

            {/* recinv-actmodal-8e4a1f67 -- one shared modal for all four actions */}
            <ConfirmationModal
                isOpen={!!confirmAction}
                onClose={() => { if (!isProcessing) setConfirmAction(null); }}
                onConfirm={runAction}
                title={confirmAction?.title || ''}
                message={confirmAction?.message || ''}
                confirmText={isProcessing ? (confirmAction?.processingText || 'Working…') : (confirmAction?.confirmText || 'Confirm')}
            />
        </div>
    );
};

export default RecurringInvoiceDetailsView;
